import React, { useEffect, useMemo, useState } from 'react';
import * as S from './styles';
import {
  Checkbox,
  FormControlLabel,
  Slider,
  Button,
} from '@material-ui/core';
import CurrencyFormat from 'react-currency-format';
import { Skeleton } from '@material-ui/lab';

// US-21: Sidebar de filtros (Localizacao, Subcategorias, Preco por pacote).
// Filtra CLIENT-SIDE sobre o result set ja carregado.
const PRICE_CAP = 5000;

const distinct = (arr) => Array.from(new Set(arr.filter(Boolean)));

const ExperienceFilter = ({ changeFilter, results, loading, error }) => {
  // opcoes derivadas dos resultados
  const localizationOptions = useMemo(
    () =>
      distinct(
        (results || []).map(
          (exp) => exp.localization || exp.city?.cityName || null
        )
      ),
    [results]
  );

  const subcategoryOptions = useMemo(
    () =>
      distinct(
        (results || []).flatMap((exp) =>
          (exp.subcategories || []).map((sub) => sub.name)
        )
      ),
    [results]
  );

  const maxPrice = useMemo(() => {
    const prices = (results || [])
      .map((exp) => Number(exp.price) || 0)
      .filter((p) => p > 0);
    if (!prices.length) return PRICE_CAP;
    // NAO capar em PRICE_CAP: experiencias sao em pontos e passam de 5000 facilmente.
    // Capar aqui fazia o range default virar [0,5000] e o slider nem alcancava os
    // exclusivos mais caros -> eles sumiam da tela (so aparecia 1). O teto = maior preco real.
    return Math.max(PRICE_CAP, Math.ceil(Math.max(...prices)));
  }, [results]);

  const [localizations, setLocalizations] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, maxPrice]);

  // ao mudar o conjunto de resultados, reseta o range de preco para o maximo disponivel
  useEffect(() => {
    setPriceRange([0, maxPrice]);
  }, [maxPrice]);

  // publica os filtros selecionados para o SearchContent/ExperienceList aplicar
  useEffect(() => {
    changeFilter({ localizations, subcategories, priceRange });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localizations, subcategories, priceRange]);

  const toggle = (list, setList, value) => {
    setList(
      list.includes(value)
        ? list.filter((item) => item !== value)
        : [...list, value]
    );
  };

  const clearAll = () => {
    setLocalizations([]);
    setSubcategories([]);
    setPriceRange([0, maxPrice]);
  };

  if (!results) {
    return loading ? (
      <Skeleton variant="rect" width={'100%'} height={1000} />
    ) : null;
  }

  if (error) return null;

  return (
    <S.Wrapper>
      <S.FilterHeader>
        <S.FilterMainTitle>Filtros</S.FilterMainTitle>
        <Button size="small" color="primary" onClick={clearAll}>
          LIMPAR
        </Button>
      </S.FilterHeader>

      {localizationOptions.length > 0 && (
        <div className="filter-group">
          <S.FilterTitle>LOCALIZAÇÃO</S.FilterTitle>
          {localizationOptions.map((opt) => (
            <FormControlLabel
              key={opt}
              control={
                <Checkbox
                  size="small"
                  checked={localizations.includes(opt)}
                  onChange={() => toggle(localizations, setLocalizations, opt)}
                />
              }
              label={opt}
            />
          ))}
        </div>
      )}

      {subcategoryOptions.length > 0 && (
        <div className="filter-group">
          <S.FilterTitle>SUBCATEGORIAS</S.FilterTitle>
          {subcategoryOptions.map((opt) => (
            <FormControlLabel
              key={opt}
              control={
                <Checkbox
                  size="small"
                  checked={subcategories.includes(opt)}
                  onChange={() => toggle(subcategories, setSubcategories, opt)}
                />
              }
              label={opt}
            />
          ))}
        </div>
      )}

      <div className="filter-group">
        <S.FilterTitle>PREÇO POR PACOTE</S.FilterTitle>
        <Slider
          value={priceRange}
          onChange={(event, newValue) => setPriceRange(newValue)}
          valueLabelDisplay="off"
          aria-labelledby="range-slider"
          min={0}
          max={maxPrice}
        />
        <S.PriceRange>
          <CurrencyFormat
            value={priceRange[0]}
            displayType={'text'}
            decimalSeparator=','
            decimalScale={2}
            prefix={'R$ '}
          />
          <span>
            <CurrencyFormat
              value={priceRange[1]}
              displayType={'text'}
              decimalSeparator=','
              decimalScale={2}
              prefix={'R$ '}
            />
          </span>
        </S.PriceRange>
      </div>
    </S.Wrapper>
  );
};

export default ExperienceFilter;
